import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Grid, Card, CardContent, Avatar, Box, Link as MuiLink, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Chip, IconButton, useMediaQuery, Drawer } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import { MessageCircle, Clock, BarChart2, BrainCircuit, Rocket, ShoppingBag, Code, FileText, Users, Server, Shield, Lock, Zap, Database, Settings, Cpu, Cloud, Layers, Network, Bot, Search, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import PageLoading from '../helpers/PageLoading';
import { landingPageContent } from '../landingpage/landingPageContent';
import { useThemeContext } from '../helpers/ThemeContext';

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.black,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: theme.palette.primary.main,
    boxShadow: theme.palette.mode === 'dark' 
      ? `0 12px 24px ${theme.palette.primary.main}25`
      : `0 12px 24px ${theme.palette.primary.main}25`
  }
}));

const HeroSection = ({ content, keycloak }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box sx={{ 
      position: 'relative', 
      minHeight: '100vh',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      overflow: 'hidden',
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
    }}>
      
      <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <GradientText 
          variant="h1" 
          component="h1" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            fontWeight: 800,
            lineHeight: 1.1
          }}
        >
          Agentic AI Platform for Universities
        </GradientText>
        
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 4,
            color: 'text.secondary',
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          Your Own Agentic AI System on Your University Infrastructure
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4,
            color: 'text.secondary',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            maxWidth: '900px',
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Deploy a complete agentic AI platform running on your university's infrastructure. Build autonomous AI agents that can reason, plan, and execute complex research and administrative tasks. Full data sovereignty with enterprise-grade AI capabilities.
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Chip 
                icon={<Check size={16} />}
                label="Agentic AI Powered"
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }}
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<Check size={16} />}
                label="On-Premise Platform"
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }}
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<Check size={16} />}
                label="University Infrastructure"
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }}
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<Check size={16} />}
                label="Research & Education Focus"
                sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <StyledButton 
            variant="contained" 
            size="large"
            onClick={() => {
              if (keycloak && !keycloak.authenticated) {
                keycloak.login();
              } else if (keycloak && keycloak.authenticated) {
                window.location.href = '/home';
              }
            }}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            Get Started
          </StyledButton>
        </Box>
      </Container>
    </Box>
  );
};

const FeaturesSection = () => {
  const theme = useTheme();
  
  const features = [
    {
      title: "Agentic AI Agents",
      description: "Build autonomous AI agents that can reason, plan, and execute complex research and administrative tasks. Multi-agent orchestration for collaborative workflows.",
      icon: "bot"
    },
    {
      title: "Research Knowledge Base",
      description: "Build and query your university's research knowledge base. Upload papers, theses, and research documents. Get AI-powered answers from your private academic repository.",
      icon: "database"
    },
    {
      title: "On-Premise University Infrastructure",
      description: "Deploy entirely on your university's infrastructure. Complete data sovereignty with no data leaving your campus. Deploy on single nodes or scale across departments.",
      icon: "server"
    },
    {
      title: "GPU Flexibility",
      description: "Deploy on any GPU infrastructure - NVIDIA, AMD, Intel. From single research workstations to university-wide GPU clusters. Complete control over your hardware and data.",
      icon: "cpu"
    },
    {
      title: "Quick Deployment",
      description: "Deploy your agentic AI platform in minutes. Pre-configured agentic capabilities ready to use on your university infrastructure.",
      icon: "rocket"
    },
    {
      title: "Education & Research Focus",
      description: "Designed specifically for universities. Support research workflows, student assistance, administrative automation, and academic knowledge management.",
      icon: "users"
    }
  ];
  
  return (
    <Box id="features" sx={{ py: 12, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard>
                <CardContent sx={{ p: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      width: 56,
                      height: 56,
                      mb: 3
                    }}
                  >
                    {getIconComponent(feature.icon, { 
                      size: 30,
                      style: { color: theme.palette.common.black }
                    })}
                  </Avatar>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const SolutionsSection = () => {
  const theme = useTheme();
  
  const solutions = [
    {
      title: "Research & Academic Workflows",
      description: "Deploy agentic AI agents to assist with research tasks, literature reviews, data analysis, and academic writing. Build knowledge bases from research papers and theses.",
      features: [
        "Agentic research assistants",
        "Academic knowledge base queries",
        "Literature review automation",
        "Research data analysis"
      ],
      icon: "fileText"
    },
    {
      title: "Student Support & Education",
      description: "Create AI agents to help students with coursework, answer questions, provide tutoring, and assist with administrative tasks. All running on your university infrastructure.",
      features: [
        "Educational AI agents",
        "Student question answering",
        "Course material assistance",
        "24/7 student support"
      ],
      icon: "users"
    },
    {
      title: "Department & Institute Use",
      description: "Enable departments and research institutes to deploy their own agentic AI systems. Multi-tenant isolation with shared infrastructure and centralized management.",
      features: [
        "Department-level deployment",
        "Multi-tenant resource isolation",
        "Centralized management",
        "Scalable across institutes"
      ],
      icon: "layers"
    },
    {
      title: "Administrative Automation",
      description: "Automate university administrative tasks with agentic AI. Handle admissions, scheduling, resource allocation, and other administrative workflows.",
      features: [
        "Administrative AI agents",
        "Workflow automation",
        "Document processing",
        "Resource management"
      ],
      icon: "settings"
    }
  ];
  
  return (
    <Box id="solutions" sx={{ py: 12 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          align="center" 
          sx={{ mb: 2, fontWeight: 700 }}
        >
          Agentic AI Solutions for Universities
        </Typography>
        <Typography 
          variant="h5" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 8 }}
        >
          Agentic AI platform designed specifically for research, education, and university operations
        </Typography>
        
        <Grid container spacing={4}>
          {solutions.map((solution, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{
                height: '100%',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      width: 56,
                      height: 56,
                      mb: 3
                    }}
                  >
                    {getIconComponent(solution.icon, { 
                      size: 30,
                      style: { color: theme.palette.common.black }
                    })}
                  </Avatar>
                  
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                    {solution.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {solution.description}
                  </Typography>
                  
                  <List>
                    {solution.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Check size={18} style={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const HowItWorksSection = ({ content }) => {
  const theme = useTheme();
  
  return (
    <Box id="how-it-works" sx={{ py: 12 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          align="center" 
          sx={{ mb: 2, fontWeight: 700 }}
        >
          {content.howItWorks.title}
        </Typography>
        <Typography 
          variant="h5" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 8 }}
        >
          {content.howItWorks.subtitle}
        </Typography>
        
        <Grid container spacing={4}>
          {content.howItWorks.steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ 
                textAlign: 'center',
                position: 'relative'
              }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800,
                    color: alpha(theme.palette.primary.main, 0.2),
                    mb: 2
                  }}
                >
                  {step.number}
                </Typography>
                
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {getIconComponent(step.icon, {
                    size: 32,
                    style: { color: theme.palette.common.black }
                  })}
                </Avatar>
                
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const InfrastructureSection = ({ content }) => {
  const theme = useTheme();
  const [activePioAgentIndex, setActivePioAgentIndex] = useState(0);
  const [activePioVoiceIndex, setActivePioVoiceIndex] = useState(0);
  
  const pioAgentFeatures = content.infrastructure.features.filter(f => f.badge === 'PioAgent');
  const pioVoiceFeatures = content.infrastructure.features.filter(f => f.badge === 'PioVoice');
  
  const FeatureCarousel = ({ features, activeIndex, setActiveIndex, badge }) => (
    <Box>
      <Chip 
        label={badge}
        sx={{ 
          mb: 1.5,
          bgcolor: badge === 'PioAgent' 
            ? alpha(theme.palette.primary.main, 0.15) 
            : alpha(theme.palette.secondary.main, 0.15),
          color: badge === 'PioAgent' 
            ? theme.palette.primary.main 
            : theme.palette.secondary.main,
          fontWeight: 700,
          fontSize: '0.7rem',
          height: '20px'
        }}
      />
      
      <Box sx={{ position: 'relative' }}>
        <Card sx={{
          bgcolor: 'background.default',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1.5,
          minHeight: '120px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                mr: 1.25
              }}
            >
              {getIconComponent(features[activeIndex].icon, {
                size: 16,
                style: { color: theme.palette.common.black }
              })}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {features[activeIndex].title}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {features[activeIndex].description}
          </Typography>
        </Card>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 0.75, alignItems: 'center' }}>
          <IconButton 
            onClick={() => setActiveIndex((activeIndex - 1 + features.length) % features.length)}
            size="small"
            sx={{ 
              bgcolor: 'background.default',
              border: `1px solid ${theme.palette.divider}`,
              width: 28,
              height: 28,
              '&:hover': { 
                bgcolor: 'background.paper',
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <ChevronLeft size={16} />
          </IconButton>
          
          {features.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: idx === activeIndex ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => setActiveIndex(idx)}
            />
          ))}
          
          <IconButton 
            onClick={() => setActiveIndex((activeIndex + 1) % features.length)}
            size="small"
            sx={{ 
              bgcolor: 'background.default',
              border: `1px solid ${theme.palette.divider}`,
              width: 28,
              height: 28,
              '&:hover': { 
                bgcolor: 'background.paper',
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
  
  return (
    <Box id="infrastructure" sx={{ py: 12, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
          {content.infrastructure.title}
        </Typography>
        <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 2 }}>
          {content.infrastructure.subtitle}
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: '800px', mx: 'auto', fontSize: '1.1rem' }}>
          {content.infrastructure.description}
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <FeatureCarousel 
              features={pioAgentFeatures}
              activeIndex={activePioAgentIndex}
              setActiveIndex={setActivePioAgentIndex}
              badge="PioAgent"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FeatureCarousel 
              features={pioVoiceFeatures}
              activeIndex={activePioVoiceIndex}
              setActiveIndex={setActivePioVoiceIndex}
              badge="PioVoice"
            />
          </Grid>
        </Grid>

        <Box sx={{ 
          py: 4, 
          px: 3, 
          bgcolor: 'background.default', 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: 4
        }}>
          <Typography variant="h4" align="center" sx={{ mb: 4, fontWeight: 600 }}>
            Built for Real-World Applications
          </Typography>
          
          <Grid container spacing={3}>
            {content.infrastructure.useCases.map((useCase, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{
                  p: 2.5,
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
                  }
                }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: 'primary.main',
                      mx: 'auto',
                      mb: 1.5
                    }}
                  >
                    {getIconComponent(useCase.icon, {
                      size: 28,
                      style: { color: theme.palette.common.black }
                    })}
                  </Avatar>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '0.95rem' }}>
                    {useCase.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {useCase.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {content.infrastructure.cta.title}
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://meetings-eu1.hubspot.com/mazda?uuid=36465083-6274-4cc0-a076-e62c90c5450c"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600, px: 4 }}
          >
            {content.infrastructure.cta.button}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};


// const VideoSection = ({ content }) => {
//   const theme = useTheme();
//   
//   return (
//     <Box id="video" sx={{ py: 12, bgcolor: 'background.paper' }}>
//       <Container maxWidth="lg">
//         <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
//           {content.videoSection.title}
//         </Typography>
//         <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
//           {content.videoSection.subtitle}
//         </Typography>
//         
//         <Box sx={{
//           position: 'relative',
//           paddingTop: '56.25%',
//           bgcolor: 'background.elevation1',
//           borderRadius: 4,
//           overflow: 'hidden',
//           border: `1px solid ${theme.palette.divider}`
//         }}>
//           <Box sx={{
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center'
//           }}>
//             <Typography variant="h4" color="text.secondary">
//               {content.videoSection.placeholder}
//             </Typography>
//           </Box>
//         </Box>
//       </Container>
//     </Box>
//   );
// };



export default function PioSphereLandingPage({ language = 'en', toggleLanguage }) {
  const { keycloak, initialized } = useKeycloak();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const content = landingPageContent[language];

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavClick = (key) => {
    setMobileMenuOpen(false);
    if (key === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const targetId = key === 'features' ? 'features' : 
                      key === 'solutions' ? 'solutions' : null;
      if (targetId) {
        const element = document.getElementById(targetId);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  if (!initialized) {
    return <PageLoading />;
  }

  if (keycloak.authenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Box sx={{ 
      width: '100vw',
      margin: 0,
      padding: 0,
      overflowX: 'hidden',
      bgcolor: 'background.default'
    }}>
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: alpha(theme.palette.background[theme.palette.mode === 'dark' ? 'default' : 'paper'], 0.9),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          top: 0,
          zIndex: 1300
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 }, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      lineHeight: 1.2
                    }}
                  >
                    University of Twente Cloud
                  </Typography>
                  <Typography 
                    variant="caption" 
                    component="div" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      fontWeight: 400,
                      lineHeight: 1.2,
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    powered by PioSphere
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2.5, alignItems: 'center', flex: '0 1 auto' }}>
              <MuiLink 
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('features');
                }}
                color="inherit" 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                Features
              </MuiLink>
              <MuiLink 
                href="#solutions"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('solutions');
                }}
                color="inherit" 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                Solutions
              </MuiLink>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flex: '0 0 auto', flexWrap: 'nowrap' }}>
              <MuiLink 
                component={Link}
                to="/documentation"
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <FileText size={16} />
                <Typography 
                  component="span"
                  sx={{ 
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    display: { xs: 'none', md: 'inline' }
                  }}
                >
                  docs
                </Typography>
              </MuiLink>
              <Button 
                variant="outlined" 
                onClick={() => keycloak.login()}
                size="small"
                sx={{ 
                  color: 'primary.main', 
                  borderColor: 'primary.main',
                  fontWeight: 500,
                  display: { xs: 'none', md: 'flex' },
                  px: 2,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {content.auth.login}
              </Button>
              
              <StyledButton 
                variant="contained"
                onClick={() => {
                  if (keycloak && !keycloak.authenticated) {
                    keycloak.login();
                  }
                }}
                size="small"
                sx={{ 
                  fontWeight: 600,
                  display: { xs: 'none', md: 'flex' },
                  px: 2,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {content.auth.signup}
              </StyledButton>
              
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  px: { xs: 0.5, sm: 1 }
                }}
              >
                {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
              
              <Button 
                variant="text"
                onClick={toggleLanguage}
                startIcon={<LanguageIcon fontSize="small" />}
                size="small"
                sx={{ 
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 'auto',
                  px: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {language === 'en' ? 'DE' : 'EN'}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileMenuOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <X size={24} />
          </IconButton>
        </Box>
        <List sx={{ pt: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavClick('features')}
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: 'primary.main'
                }
              }}
            >
              <ListItemText primary="Features" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavClick('solutions')}
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: 'primary.main'
                }
              }}
            >
              <ListItemText primary="Solutions" />
            </ListItemButton>
          </ListItem>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <MuiLink 
                component={Link}
                to="/documentation"
                onClick={handleDrawerToggle}
                sx={{ 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <FileText size={16} />
                <Typography 
                  component="span"
                  sx={{ 
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}
                >
                  docs
                </Typography>
              </MuiLink>
            </Box>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => {
                handleDrawerToggle();
                keycloak.login();
              }}
              sx={{ 
                mb: 2,
                color: 'primary.main', 
                borderColor: 'primary.main',
                fontWeight: 500
              }}
            >
              {content.auth.login}
            </Button>
            <StyledButton 
              variant="contained"
              fullWidth
              onClick={() => {
                handleDrawerToggle();
                if (keycloak && !keycloak.authenticated) {
                  keycloak.login();
                }
              }}
              sx={{ 
                fontWeight: 600
              }}
            >
              {content.auth.signup}
            </StyledButton>
            <Box sx={{ px: 2, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${theme.palette.divider}`, mt: 2 }}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              <Button 
                variant="text"
                onClick={toggleLanguage}
                startIcon={<LanguageIcon />}
                sx={{ 
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {language === 'en' ? 'DE' : 'EN'}
              </Button>
            </Box>
          </Box>
        </List>
      </Drawer>

      <Box sx={{ pt: 8 }}>
        <HeroSection content={content} keycloak={keycloak} />
        <FeaturesSection />
        <SolutionsSection />

        <Box sx={{ py: 12 }}>
          <Container maxWidth="md">
            <Box sx={{ 
              textAlign: 'center',
              p: 6,
              bgcolor: 'background.paper',
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
                Deploy Your Agentic AI Platform
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Agentic AI platform for universities - research, education, and operations on your infrastructure
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <StyledButton 
                  variant="contained" 
                  size="large"
                  onClick={() => {
                    if (keycloak && !keycloak.authenticated) {
                      keycloak.login();
                    }
                  }}
                  sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                >
                  Get Started
                </StyledButton>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box component="footer" sx={{ py: 8, bgcolor: 'background.elevation1' }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              textAlign: 'center',
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 4
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Powered by
              </Typography>
              <MuiLink 
                href="https://piosphere.ai"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': { 
                    color: 'primary.dark',
                    textDecoration: 'underline'
                  }
                }}
              >
                PioSphere
                <Box component="span" sx={{ ml: 0.5, fontSize: '0.875rem' }}>↗</Box>
              </MuiLink>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Enterprise AI infrastructure platform
              </Typography>
            </Box>
            
            <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <Grid container spacing={2} justifyContent="center">
                {content.footer.legal.links.map((link, index) => (
                  <Grid item key={index}>
                    <MuiLink 
                      sx={{ 
                        cursor: 'pointer',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {link}
                    </MuiLink>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

function getIconComponent(iconName, props) {
  const iconMap = {
    zap: Zap,
    brainCircuit: BrainCircuit,
    code: Code,
    rocket: Rocket,
    clock: Clock,
    shoppingBag: ShoppingBag,
    messageCircle: MessageCircle,
    fileText: FileText,
    users: Users,
    bot: Bot,
    search: Search,
    settings: Settings,
    cpu: Cpu,
    cloud: Cloud,
    layers: Layers,
    network: Network,
    database: Database,
    lock: Lock,
    shield: Shield,
    barChart2: BarChart2,
    server: Server
  };
  
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent {...props} /> : null;
}